import React, { useState, useEffect } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Package, Pencil, Plus, Clock, BookOpen, Tv2, Trash2, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import CreateModuleModal from '@/pages/Dashboard/components/CreateModuleModal'

interface Module {
  id: number
  name: string
  coachFlowId: number
  roleplayFlowId: number
  reviewPromptPayloadId: number
  timerDuration: number
  defaultRemainingTime: number
  reviewPageSize: number
  microfankProjectId?: number | null
  createdAt?: string
}

const DELETE_MODULE = gql`
  mutation deleteModule($moduleId: Int!) {
    deleteModule(moduleId: $moduleId) {
      success
      message
    }
  }
`

const CLEAR_MODULE_WELCOME_CACHE = gql`
  mutation clearModuleWelcomeCache($moduleId: Int!) {
    clearModuleWelcomeCache(moduleId: $moduleId) {
      success
      message
    }
  }
`

const GET_MODULES = gql`
  query getModules {
    modules: getModules {
      id
      name
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      microfankProjectId
      createdAt
    }
  }
`

interface ModulesViewProps {
  onModulesChanged?: () => void
}

const ModulesView: React.FC<ModulesViewProps> = ({ onModulesChanged }) => {
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editModule, setEditModule] = useState<Module | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [clearingCacheId, setClearingCacheId] = useState<number | null>(null)
  const { showSuccess, showError } = useToast()

  const fetchModules = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await authorizedGraphQLRequest<{ modules: Module[] }>(GET_MODULES)
      setModules(res.modules || [])
    } catch {
      setError('Failed to load modules')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchModules()
  }, [])

  const handleEdit = (module: Module) => {
    setEditModule(module)
    setIsModalOpen(true)
  }

  const handleClearCache = async (module: Module) => {
    // Bulk action: wipes every target's welcome in the module, so confirm before running.
    if (!confirm(`Reset welcome cache for all targets in "${module.name}"? They will regenerate on next open.`)) return
    try {
      setClearingCacheId(module.id)
      await authorizedGraphQLRequest(CLEAR_MODULE_WELCOME_CACHE, { moduleId: module.id })
      showSuccess('Module welcome cache cleared')
    } catch (error) {
      console.error('Failed to clear module welcome cache:', error)
      showError('Failed to clear module welcome cache')
    } finally {
      setClearingCacheId(null)
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditModule(null)
  }

  const handleDeleteConfirm = async (moduleId: number) => {
    try {
      setDeleting(true)
      setDeleteError(null)
      await authorizedGraphQLRequest(DELETE_MODULE, { moduleId })
      setConfirmDeleteId(null)
      fetchModules()
      onModulesChanged?.()
    } catch (err: any) {
      const message = err?.response?.errors?.[0]?.message || 'Failed to delete module'
      setDeleteError(message)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 mb-4">{error}</p>
        <Button variant="outline" onClick={fetchModules}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-800">All Modules ({modules.length})</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditModule(null)
              setIsModalOpen(true)
            }}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Create Module
          </Button>
        </div>

        {modules.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">No modules yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first module to get started</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {modules.map((module) => (
              <div key={module.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                {/* Card Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 p-2 bg-gradient-to-br from-primary to-primary/70 rounded-lg">
                      <Package className="h-4 w-4 text-white" />
                    </div>
                    <h5 className="font-semibold text-gray-800 truncate text-sm">{module.name}</h5>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(module)
                      }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Edit module"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleClearCache(module)
                      }}
                      disabled={clearingCacheId === module.id}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                      title="Reset welcome cache"
                    >
                      <RotateCcw className={`h-3.5 w-3.5 ${clearingCacheId === module.id ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setConfirmDeleteId(module.id)
                        setDeleteError(null)
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete module"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Inline delete confirm */}
                {confirmDeleteId === module.id && (
                  <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700 font-medium mb-2">Delete this module?</p>
                    {deleteError && <p className="text-xs text-red-600 mb-2">{deleteError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteConfirm(module.id)}
                        disabled={deleting}
                        className="px-2.5 py-1 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {deleting ? 'Deleting…' : 'Delete'}
                      </button>
                      <button
                        onClick={() => {
                          setConfirmDeleteId(null)
                          setDeleteError(null)
                        }}
                        disabled={deleting}
                        className="px-2.5 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Config chips */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
                    <Tv2 className="h-3 w-3" />
                    Coach {module.coachFlowId}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                    <Tv2 className="h-3 w-3" />
                    RP {module.roleplayFlowId}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">
                    <BookOpen className="h-3 w-3" />
                    Prompt {module.reviewPromptPayloadId}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                    <Clock className="h-3 w-3" />
                    {module.timerDuration}s
                  </span>
                </div>

                {/* Footer */}
                <div className="text-[10px] text-gray-400 flex items-center justify-between pt-2 border-t border-gray-100">
                  <span>ID: {module.id}</span>
                  {module.microfankProjectId && <span className="text-teal-600">Project {module.microfankProjectId}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateModuleModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={() => {
          fetchModules()
          onModulesChanged?.()
        }}
        editModule={editModule}
      />
    </>
  )
}

export default ModulesView
